import { useMemo } from 'react'
import { getWrappedNativeAddress } from 'uniswap/src/constants/addresses'
import {
  Chain,
  SafetyLevel,
  SearchPopularTokensQuery,
  useSearchPopularTokensQuery,
} from 'uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks'
import { useEnabledChains } from 'uniswap/src/features/chains/hooks/useEnabledChains'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { areAddressesEqual } from 'uniswap/src/utils/addresses'

export type TopToken = NonNullable<NonNullable<SearchPopularTokensQuery['topTokens']>[0]>

// Popular tokens by top Uniswap trading volume
export function usePopularTokens(): {
  popularTokens: TopToken[] | undefined
  loading: boolean
  error: any
} {
  // Load popular tokens by top Uniswap trading volume
  const { data, loading, error } = useSearchPopularTokensQuery()
  const { defaultChainId } = useEnabledChains()


  // H2链测试代币数据
  const h2TestTokens = useMemo(() => {
    if (defaultChainId !== UniverseChainId.H2) return null
    
    return [
      {
        id: "0x1755732dacee3e4d38cddab3a1e51f30aad28f86",
        address: "0x1755732dacee3e4d38cddab3a1e51f30aad28f86",
        chain: Chain.H2,
        symbol: "WETH",
        name: "Wrapped Ether",
        decimals: 18,
        standard: "ERC20",
        __typename: "Token" as const,
        project: {
          id: "weth-h2",
          logoUrl: null,
          safetyLevel: SafetyLevel.Verified,
          isSpam: false,
          __typename: "TokenProject" as const
        }
      },
      {
        id: "0xeeba5019f59c00c829ffa51a93dbf4fd4b539980",
        address: "0xeeba5019f59c00c829ffa51a93dbf4fd4b539980",
        chain: Chain.H2,
        symbol: "BTC",
        name: "BTC",
        decimals: 18,
        standard: "ERC20",
        __typename: "Token" as const,
        project: {
          id: "btc-h2",
          logoUrl: null,
          safetyLevel: SafetyLevel.Verified,
          isSpam: false,
          __typename: "TokenProject" as const
        }
      },
      {
        id: "0xa0b86a33e6776c8a8b6c5c4b5e4b2e8f7c9d0e1f",
        address: "0xa0b86a33e6776c8a8b6c5c4b5e4b2e8f7c9d0e1f",
        chain: Chain.H2,
        symbol: "USDT",
        name: "Tether USD",
        decimals: 6,
        standard: "ERC20",
        __typename: "Token" as const,
        project: {
          id: "usdt-h2",
          logoUrl: null,
          safetyLevel: SafetyLevel.Verified,
          isSpam: false,
          __typename: "TokenProject" as const
        }
      },
      {
        id: "0xb1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0",
        address: "0xb1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0",
        chain: Chain.H2,
        symbol: "USDC",
        name: "USD Coin",
        decimals: 6,
        standard: "ERC20",
        __typename: "Token" as const,
        project: {
          id: "usdc-h2",
          logoUrl: null,
          safetyLevel: SafetyLevel.Verified,
          isSpam: false,
          __typename: "TokenProject" as const
        }
      }
    ] as TopToken[]
  }, [defaultChainId])

  const popularTokens = useMemo(() => {
    // 如果是H2链，直接返回测试数据
    if (defaultChainId === UniverseChainId.H2) {
      return h2TestTokens
    }

    if (!data || !data.topTokens) {
      return undefined
    }

    // special case to replace weth with eth because the backend does not return eth data
    // eth will be defined only if all the required data is available
    // when eth data is not fully available, we do not replace weth with eth
    const eth = data?.eth && data?.eth.length > 0 && data?.eth?.[0]?.project ? data.eth[0] : null
    const wethAddress = getWrappedNativeAddress(defaultChainId)

    return data.topTokens
      .map((token) => {
        if (!token) {
          return undefined
        }

        const isWeth = areAddressesEqual(token.address, wethAddress) && token?.chain === Chain.Ethereum

        // manually replace weth with eth given backend only returns eth data as a proxy for eth
        if (isWeth && eth) {
          return eth
        }

        return token
      })
      .filter((t): t is TopToken => Boolean(t))
  }, [data, defaultChainId, h2TestTokens])

  const result = { 
    popularTokens, 
    loading: defaultChainId === UniverseChainId.H2 ? false : loading,
    error: defaultChainId === UniverseChainId.H2 ? null : error
  }


  return result
}
